import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Grid2, Typography, Button, Paper, Chip } from '@mui/material';
import {
  Building2,
  MessageSquare,
  PhoneCall,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  Users,
  Cpu,
  Zap,
  Globe,
  Layers,
  BarChart3,
} from 'lucide-react';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const productWebsiteLinks = {
  acolead: 'https://acolead.com',
  instagramFacebook: 'https://acolead.com/modules/instagram',
  whatsapp: 'https://acolead.com/modules/whatsapp',
  aiCalling: 'https://acolead.com/modules/ai-calling',
};

const ProductsPage = () => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* ========================================================================= */}
      {/* 1. HERO SECTION */}
      {/* ========================================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'radial-gradient(ellipse at 50% 0%, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', sm: '3.4rem', md: '4.2rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.035em',
                color: '#0F172A',
                mb: 3,
              }}
            >
              Products Built Around <span className="text-gradient-emerald">Real Business Needs</span>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#334155',
                lineHeight: 1.7,
                maxWidth: 760,
                mx: 'auto',
              }}
            >
              Our products are developed around practical business challenges, combining automation, intelligent workflows, and easy-to-use technology to help teams work more effectively.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 2. ACOLEAD CRM PLATFORM DEEP-DIVE */}
      {/* ========================================================================= */}
      <Box id="acolead-crm" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 5,
              background: 'linear-gradient(135deg, #070D19 0%, #0F172A 100%)',
              color: '#FFFFFF',
              mb: 8,
              border: '1px solid rgba(255, 255, 255, 0.12)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                borderColor: 'rgba(96, 165, 250, 0.4)',
              },
            }}
          >
            <Grid2 container spacing={4} alignItems="center">
              <Grid2 size={{ xs: 12, md: 7 }}>
                <Chip
                  label="FLAGSHIP PRODUCT"
                  sx={{
                    backgroundColor: 'rgba(37, 99, 235, 0.25)',
                    color: '#60A5FA',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    mb: 2,
                    border: '1px solid rgba(96, 165, 250, 0.4)',
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2.2rem', md: '3rem' },
                    color: '#FFFFFF',
                    mb: 2,
                    letterSpacing: '-0.03em',
                  }}
                >
                  Acolead CRM
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#60A5FA',
                    mb: 3,
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                  }}
                >
                  AI-Powered Real Estate CRM & Sales Automation Platform
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8', mb: 4, fontSize: '1.05rem', lineHeight: 1.7 }}>
                  AcoLead helps real estate businesses manage leads, follow-ups, properties, site visits, team activities, customer communication, and sales visibility through one connected platform.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  component="a"
                  href={productWebsiteLinks.acolead}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={18} />}
                  sx={{
                    py: 1.4,
                    px: 3.5,
                    borderRadius: '10px',
                    fontWeight: 600,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  Visit AcoLead Website
                </Button>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 5 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3.5,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 2 }}>
                    Purpose-Built For:
                  </Typography>
                  {[
                    'Real Estate Builders',
                    'Property Developers',
                    'Channel Partners & Broker Networks',
                    'Real Estate Agencies',
                    'Mandate Sales Firms',
                    'In-House Sales Teams',
                  ].map((persona, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <CheckCircle2 size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#E2E8F0', fontWeight: 600, fontSize: '0.925rem' }}>
                        {persona}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid2>
            </Grid2>
          </Paper>

          {/* Acolead Capabilities Grid */}
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mb: 4, textAlign: 'center' }}>
            Acolead Capabilities Breakdown
          </Typography>
          <Grid2 container spacing={3}>
            {[
              { title: 'Lead Management', desc: 'Consolidate enquiries from Meta ads, real estate portals, website forms, and direct walk-ins.', icon: <Users size={22} /> },
              { title: 'Smart Lead Assignment', desc: 'Instant automated lead distribution via customizable round-robin logic to available agents.', icon: <Cpu size={22} /> },
              { title: 'Follow-ups & Scheduling', desc: 'Automated task reminders, site visit booking calendars, and caller outcome logs.', icon: <Zap size={22} /> },
              { title: 'Inventory Management', desc: 'Real-time property unit availability, block releases, pricing tiers, and booking states.', icon: <Building2 size={22} /> },
              { title: 'Team Management', desc: 'Multi-level role permissions, telecaller audit logs, and performance metric dashboards.', icon: <Layers size={22} /> },
              { title: 'Channel Partner Portal', desc: 'Empower broker networks with dedicated lead submission portals and payout transparency.', icon: <Globe size={22} /> },
              { title: 'WhatsApp Communication', desc: 'Integrated WhatsApp messaging inside lead profiles to send brochures and site plans.', icon: <MessageSquare size={22} /> },
              { title: 'Reports & Analytics', desc: 'Executive visibility into lead conversion rates, agent velocity, and sales forecasting.', icon: <BarChart3 size={22} /> },
            ].map((cap, idx) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Paper
                  elevation={0}
                  className="card-interactive"
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {cap.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0F172A', mb: 1 }}>
                    {cap.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {cap.desc}
                  </Typography>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 3. DEDICATED AI SOLUTIONS SECTIONS (Clean, Professional Light Theme) */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FAFCFF', borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="INTELLIGENT WORKFLOW AUTOMATION"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#1E40AF',
                fontWeight: 700,
                fontSize: '0.76rem',
                letterSpacing: '0.08em',
                border: '1px solid rgba(37, 99, 235, 0.18)',
                mb: 2,
                py: 0.5,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', md: '2.8rem' },
                color: '#0F172A',
                letterSpacing: '-0.025em',
                mb: 2,
              }}
            >
              AcoLead Add-On Solutions
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 680, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
              Extend the AcoLead ecosystem with intelligent communication and automation capabilities designed around the modern real estate sales journey.
            </Typography>
          </Box>

          <Grid2 container spacing={6}>
            {/* 1. Instagram & Facebook AI */}
            <Grid2 size={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5.5 },
                  borderRadius: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(225, 48, 108, 0.16)',
                  boxShadow: '0 20px 40px -10px rgba(225, 48, 108, 0.06)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(225, 48, 108, 0.35)',
                    boxShadow: '0 25px 50px -12px rgba(225, 48, 108, 0.12)',
                  },
                }}
              >
                <Grid2 container spacing={4} alignItems="center">
                  <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: { xs: 66, md: 48 },height: { xs: 50, md: 48 },
                          borderRadius: { xs: 1, md: 2.5 },
                          backgroundColor: 'rgba(225, 48, 108, 0.1)',
                          color: '#E1306C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <InstagramIcon style={{ fontSize: 40 }} />
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: '1rem', md: '1.8rem' } }}>
                          Instagram & Facebook AI
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#E1306C', fontWeight: 700 }}>
                          Turn social conversations into sales opportunities.
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, mb: 3.5, fontSize: '1rem' }}>
                      Engage with enquiries from Instagram and Facebook, automate conversations, respond to comments and messages, and bring potential customers into a structured lead-management workflow.
                    </Typography>

                    <Grid2 container spacing={1.5} sx={{ mb: 4 }}>
                      {[
                        'Automated Customer Responses',
                        'Conversation Management',
                        'AI-Powered Engagement',
                        'Lead Interaction Support',
                        'Social Media Communication Automation',
                      ].map((feature, i) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle2 size={16} style={{ color: '#E1306C' }} />
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem' }}>
                              {feature}
                            </Typography>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>

                    <Button
                      variant="contained"
                      component="a"
                      href={productWebsiteLinks.instagramFacebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<ArrowRight size={18} />}
                      sx={{
                        py: 1.3,
                        px: 3.5,
                        borderRadius: '12px',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #E1306C 0%, #C13584 100%)',
                        boxShadow: '0 10px 20px -5px rgba(225, 48, 108, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #C13584 0%, #833AB4 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 28px -5px rgba(225, 48, 108, 0.4)',
                        },
                      }}
                    >
                      Explore Solution
                    </Button>
                  </Grid2>

                  {/* Clean Light-Themed Simulation Card */}
                  <Grid2 size={{ xs: 12, md: 5 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 3.5 },
                        borderRadius: 3.5,
                        backgroundColor: '#FFF5F8',
                        border: '1px solid rgba(225, 48, 108, 0.2)',
                        boxShadow: '0 10px 30px rgba(225, 48, 108, 0.05)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 16px 40px rgba(225, 48, 108, 0.08)',
                          borderColor: 'rgba(225, 48, 108, 0.35)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: { xs: 0.75, sm: 1.5 },
                          mb: 2,
                          minWidth: 0,
                        }}
                      >
                        <Chip
                          label="AI SOCIAL AUTOMATION"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(225, 48, 108, 0.15)',
                            color: '#BE185D',
                            fontWeight: 800,
                            fontSize: { xs: '0.58rem', sm: '0.7rem' },
                            border: '1px solid rgba(225, 48, 108, 0.25)',
                            flexShrink: 1,
                            minWidth: 0,
                            '& .MuiChip-label': {
                              px: { xs: 1, sm: 1.5 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: 'max-content',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.62rem', sm: '0.75rem' },
                            fontWeight: 700,
                            color: '#059669',
                            flexShrink: 0,
                          }}
                        >
                          <Box component="span" aria-hidden="true">●</Box>
                          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                            Active Lead Inflow
                          </Box>
                        </Typography>
                      </Box>

                      {/* Inbound Comment */}
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(225, 48, 108, 0.15)', mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#BE185D', fontWeight: 700, display: 'block', mb: 0.3 }}>
                          Ad Comment Received
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontSize: { xs: '0.95rem', sm: '0.88rem' }, lineHeight: 1.65, fontWeight: 500 }}>
                          "Interested in the 3 BHK layout and launch price at Downtown Heights."
                        </Typography>
                      </Box>

                      {/* AI Instant Dispatch */}
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 800, display: 'block', mb: 0.3 }}>
                          ⚡ Instant AI DM Triggered (0.3s)
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontSize: { xs: '0.95rem', sm: '0.86rem' }, lineHeight: 1.65 }}>
                          "Hi Vikram! We've sent the complete PDF brochure, pricing lock sheet, and site tour booking link directly to your inbox."
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* 2. WhatsApp Business API & AI */}
            <Grid2 size={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5.5 },
                  borderRadius: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(37, 211, 102, 0.2)',
                  boxShadow: '0 20px 40px -10px rgba(37, 211, 102, 0.06)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(37, 211, 102, 0.4)',
                    boxShadow: '0 25px 50px -12px rgba(37, 211, 102, 0.12)',
                  },
                }}
              >
                <Grid2 container spacing={4} alignItems="center">
                  <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: { xs: 74, md: 58 },
                          height: { xs: 50, md: 58 },
                          borderRadius: { xs: 1, md: 2.5 },
                          backgroundColor: 'rgba(37, 211, 102, 0.12)',
                          color: '#16A34A',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WhatsAppIcon sx={{ fontSize: 40 }} />
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: '0.9rem', md: '1.8rem' } }}>
                          WhatsApp Business API & AI
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#16A34A', fontWeight: 700, fontSize: { xs: '0.75rem', md: '0.9rem' } }}>
                          Make customer communication faster and more structured.
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, mb: 3.5, fontSize: '1rem' }}>
                      Enable real estate teams to manage WhatsApp-based customer communication, automate repetitive interactions, run supported campaigns, and connect conversations with their sales workflow.
                    </Typography>

                    <Grid2 container spacing={1.5} sx={{ mb: 4 }}>
                      {[
                        'Official WhatsApp Business API',
                        'Automated Messaging Sequences',
                        'AI-Powered Conversations',
                        'Follow-up Automation',
                        'Business Communication Infrastructure',
                      ].map((feature, i) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle2 size={16} style={{ color: '#16A34A' }} />
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem' }}>
                              {feature}
                            </Typography>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>

                    <Button
                      variant="contained"
                      component="a"
                      href={productWebsiteLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<ArrowRight size={18} />}
                      sx={{
                        py: 1.3,
                        px: 3.5,
                        borderRadius: '12px',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #16A34A 0%, #059669 100%)',
                        boxShadow: '0 10px 20px -5px rgba(22, 163, 74, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 28px -5px rgba(22, 163, 74, 0.4)',
                        },
                      }}
                    >
                      Explore Solution
                    </Button>
                  </Grid2>

                  {/* Clean Light-Themed WhatsApp Simulation Card */}
                  <Grid2 size={{ xs: 12, md: 5 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 3.5 },
                        borderRadius: 3.5,
                        backgroundColor: '#F0FDF4',
                        border: '1px solid rgba(34, 197, 94, 0.25)',
                        boxShadow: '0 10px 30px rgba(34, 197, 94, 0.05)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 16px 40px rgba(34, 197, 94, 0.08)',
                          borderColor: 'rgba(34, 197, 94, 0.4)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: { xs: 0.75, sm: 1.5 },
                          mb: 2,
                          minWidth: 0,
                        }}
                      >
                        <Chip
                          label="WHATSAPP API AGENT"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(34, 197, 94, 0.18)',
                            color: '#15803D',
                            fontWeight: 800,
                            fontSize: { xs: '0.58rem', sm: '0.7rem' },
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            flexShrink: 1,
                            minWidth: 0,
                            '& .MuiChip-label': {
                              px: { xs: 1, sm: 1.5 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: '0.62rem', sm: '0.75rem' },
                            fontWeight: 700,
                            color: '#15803D',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          ✓ Meta Verified API
                        </Typography>
                      </Box>

                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(34, 197, 94, 0.2)', mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#15803D', fontWeight: 700, display: 'block', mb: 0.3 }}>
                          Customer Query Received
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontSize: { xs: '0.95rem', sm: '0.88rem' }, lineHeight: 1.65, fontWeight: 500 }}>
                          "Can you share the price list and possession schedule for Tower C?"
                        </Typography>
                      </Box>

                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(37, 99, 235, 0.25)' }}>
                        <Typography variant="caption" sx={{ color: '#2563EB', fontWeight: 800, display: 'block', mb: 0.3 }}>
                          🤖 Automated Document Dispatch
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontSize: { xs: '0.95rem', sm: '0.86rem' }, lineHeight: 1.65 }}>
                          "Here is the official payment schedule and verified brochure. Would you like to schedule a site visit this Saturday?"
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* 3. AI Calling Solutions */}
            <Grid2 size={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5.5 },
                  borderRadius: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(79, 70, 229, 0.16)',
                  boxShadow: '0 20px 40px -10px rgba(79, 70, 229, 0.06)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(79, 70, 229, 0.35)',
                    boxShadow: '0 25px 50px -12px rgba(79, 70, 229, 0.12)',
                  },
                }}
              >
                <Grid2 container spacing={4} alignItems="center">
                  <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: { xs: 50, md: 48 },height: { xs: 47, md: 48 },
                          borderRadius: { xs: 1, md: 2.5 },
                          backgroundColor: 'rgba(79, 70, 229, 0.1)',
                          color: '#4F46E5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PhoneCall size={26} />
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: '1.4rem', md: '1.8rem' } }}>
                          AI Calling Solutions
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#4F46E5', fontWeight: 700 }}>
                          Bring AI-powered calling into your sales workflow.
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, mb: 3.5, fontSize: '1rem' }}>
                      Use AI-driven calling capabilities to support customer outreach, automate selected calling workflows, and keep call-related information connected with the sales process.
                    </Typography>

                    <Grid2 container spacing={1.5} sx={{ mb: 4 }}>
                      {[
                        'Lead Engagement Automation',
                        'Customer Communication Workflows',
                        'Follow-up & Reminder Support',
                        'Automated Calling Workflows',
                        'Business Communication Integration',
                      ].map((feature, i) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle2 size={16} style={{ color: '#4F46E5' }} />
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem' }}>
                              {feature}
                            </Typography>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>

                    <Button
                      variant="contained"
                      component="a"
                      href={productWebsiteLinks.aiCalling}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<ArrowRight size={18} />}
                      sx={{
                        py: 1.3,
                        px: 3.5,
                        borderRadius: '12px',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #4F46E5 0%, #2563EB 100%)',
                        boxShadow: '0 10px 20px -5px rgba(79, 70, 229, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 28px -5px rgba(79, 70, 229, 0.4)',
                        },
                      }}
                    >
                      Explore Solution
                    </Button>
                  </Grid2>

                  {/* Clean Light-Themed Voice AI Simulation Card */}
                  <Grid2 size={{ xs: 12, md: 5 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 3.5 },
                        borderRadius: 3.5,
                        backgroundColor: '#EEF2FF',
                        border: '1px solid rgba(99, 102, 241, 0.25)',
                        boxShadow: '0 10px 30px rgba(99, 102, 241, 0.05)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 16px 40px rgba(99, 102, 241, 0.08)',
                          borderColor: 'rgba(99, 102, 241, 0.4)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: { xs: 0.75, sm: 1.5 },
                          mb: 2,
                          minWidth: 0,
                        }}
                      >
                        <Chip
                          label="VOICE AI CALLING ENGINE"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(99, 102, 241, 0.18)',
                            color: '#4338CA',
                            fontWeight: 800,
                            fontSize: { xs: '0.58rem', sm: '0.7rem' },
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            flexShrink: 1,
                            minWidth: 0,
                            '& .MuiChip-label': {
                              px: { xs: 1, sm: 1.5 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: '0.62rem', sm: '0.75rem' },
                            fontWeight: 700,
                            color: '#4338CA',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          ● Instant Inbound Callback
                        </Typography>
                      </Box>

                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(99, 102, 241, 0.2)', mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#4338CA', fontWeight: 700, display: 'block', mb: 0.3 }}>
                          Natural Language Voice Model
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontSize: { xs: '0.95rem', sm: '0.88rem' }, lineHeight: 1.65, fontWeight: 500 }}>
                          "Hi Priya, this is Coalitionify AI following up on your digital enquiry for Grand Horizon Towers..."
                        </Typography>
                      </Box>

                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, backgroundColor: '#FFFFFF', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 800, display: 'block', mb: 0.3 }}>
                          ✓ Qualified & Synced to Acolead CRM
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontSize: { xs: '0.95rem', sm: '0.86rem' }, lineHeight: 1.65 }}>
                          "Customer confirmed site visit for Saturday at 11:00 AM. Budget: ₹1.4 Cr. Assigned to Senior Closing Manager."
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 4. ACOLEAD CUSTOMIZATION */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(145deg, #EFF6FF 0%, #F0FDFA 100%)',
              border: '1px solid rgba(37, 99, 235, 0.14)',
            }}
          >
            <Chip
              label="CONFIGURED FOR YOUR BUSINESS"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                color: '#1D4ED8',
                fontWeight: 800,
                fontSize: '0.72rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: '1rem', md: '2.4rem' }, mb: 2 }}>
              Technology That Adapts to Your Business
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.75, mb: 2 }}>
              Every real estate business operates differently. Depending on business requirements and product feasibility, AcoLead can be configured and customized to support specific workflows, processes, and operational needs.
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.75, mb: 3.5 }}>
              Our team works closely with businesses to understand their requirements and identify the right product configuration for their sales and operational processes.
            </Typography>
            <Button
              variant="contained"
              component="a"
              href={productWebsiteLinks.acolead}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<ExternalLink size={17} />}
              sx={{ py: 1.3, px: 3.5, borderRadius: '10px', fontWeight: 700 }}
            >
              Visit AcoLead Website
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductsPage;